// The subject a page's title names, shared by the server-rendered head
// (`server-seo.ts`) and the SPA's `document.title` on every navigation. Both
// compose it with `composeTitle` from `seo-text.ts`. Two tables would drift,
// and GA4 records whichever string the tab carries: a page whose first load
// and in-app arrival disagree reports as two pages.
//
// Section subjects are English on purpose, whatever the viewer's locale. The
// title is an analytics dimension as much as a tab label, and a localised one
// would split every page into one row per language.

import circlesCopy from '../../messages/en/circles.json';
import invitationsCopy from '../../messages/en/invitations.json';
import networksCopy from '../../messages/en/networks.json';
import notificationsCopy from '../../messages/en/notifications.json';
import resourcesCopy from '../../messages/en/resources.json';
import searchCopy from '../../messages/en/search.json';
import shellCopy from '../../messages/en/shell.json';

// Every subject here is product copy, never member-authored text. The title
// reaches GA4 on every event (gtag reads `document.title`), and Google's
// terms forbid sending it anything that could identify a person, so a page
// about a member or a member's post is titled by its section, not its
// subject. Entity names reach the title only through a route loader's
// `pageTitle`, and only on the editorial routes that return one: resources and
// learning tracks.
//
// The fixed public pages can also return a `pageTitle`, but it is not an
// entity name: it is the search title an editor wrote for that page
// (`cachedStaticPageTitle`), which is product copy like the subjects below.
//
// Matched by whole path segment, so `/join` does not claim `/join-a-circle`.
const SECTION_SUBJECTS: ReadonlyArray<readonly [prefix: string, subject: string]> = [
  ['/admin', shellCopy.nav.adminPanel],
  ['/circles', circlesCopy.chrome.title],
  ['/coach', shellCopy.nav.careerCoach],
  ['/community-guidelines', 'Community Guidelines'],
  ['/events', shellCopy.nav.events],
  ['/feed', shellCopy.nav.home],
  ['/groups', shellCopy.nav.groups],
  ['/help', shellCopy.nav.help],
  ['/invitations', invitationsCopy.page.title],
  ['/join-a-circle', 'Join a Circle'],
  ['/join-a-network', 'Join a Network'],
  ['/join', 'Join Lean In'],
  ['/members', 'Members'],
  ['/messages', shellCopy.nav.messages],
  ['/moderation', shellCopy.nav.moderation],
  ['/networks', networksCopy.chrome.title],
  ['/notifications', notificationsCopy.title],
  ['/onboarding', 'Onboarding'],
  ['/people', shellCopy.nav.people],
  ['/posts', 'Posts'],
  ['/privacy', 'Privacy Policy'],
  ['/profile', 'Profile'],
  ['/resources', resourcesCopy.list.title],
  ['/roadmap', shellCopy.nav.roadmap],
  ['/search', searchCopy.results.title],
  ['/settings', shellCopy.nav.settings],
  ['/start-a-circle', 'Start a Circle'],
  ['/terms', 'Terms of Service'],
];

// Search titles for the indexable public pages, matched by exact path only so
// a page beneath one keeps its section's label (`/circles` is titled for
// search; `/circles/{id}/members` stays "Circles"). These are what a searcher
// reads in a result, so they name what the page offers rather than repeating
// a nav label, and they lead with the words a searcher scans for. Each
// subject fits 42 characters, which keeps the brand suffix inside the 60
// character title budget (`composeTitle`). Google may substitute the page's
// heading when the two disagree, so a page whose heading is its name (the
// policies) is titled by that same name.
const PUBLIC_PAGE_SUBJECTS: Readonly<Record<string, string>> = Object.freeze({
  '/': 'Free Circles, Networks & Career Resources',
  '/circles': 'Browse Lean In Circles Near You or Online',
  '/networks': 'Find a Lean In Network by City or Industry',
  '/resources': 'Free Career and Leadership Resources',
  '/join': 'Join Free: Career Resources and Community',
  '/start-a-circle': 'Start a Free Lean In Circle',
  '/join-a-circle': 'Join a Free Lean In Circle',
  '/join-a-network': 'Join a Free Lean In Network',
  '/help': 'Help and Support',
});

// The section a path belongs to, or null for a path that is only the brand
// (auth, invite links).
function sectionSubject(pathname: string): string | null {
  const match = SECTION_SUBJECTS.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return match === undefined ? null : match[1];
}

// The subject a page is titled by when its route supplies none of its own:
// the public page's search title, else its section.
export function pageSubject(pathname: string): string | null {
  return Object.hasOwn(PUBLIC_PAGE_SUBJECTS, pathname)
    ? PUBLIC_PAGE_SUBJECTS[pathname]!
    : sectionSubject(pathname);
}
